import React, { useState } from 'react';
import axios from 'axios';
import axiosInstance from '../../../config/axiosInstance';
import ENDPOINTS from '../../../config/apiUrls';


const SearchUser = () => {
  const [searchCriteria, setSearchCriteria] = useState({
    firstName: '',
    lastName: '',
    username: ''
  });
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({
    chatData: null,
    pollsData: null,
    loadingDetails: false
  });
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const searchUsers = async () => {
    // Validate that at least one search criteria is provided
    if (!searchCriteria.firstName && !searchCriteria.lastName && !searchCriteria.username) {
      setError('Please enter at least one search criteria');
      return;
    }

    setLoading(true);
    setError('');
    setSearchResults([]);
    setSelectedUser(null);
    setUserDetails({
      chatData: null,
      pollsData: null,
      loadingDetails: false
    });

    try {
      // Build query parameters
      const params = {};
      if (searchCriteria.firstName) params.firstName = searchCriteria.firstName;
      if (searchCriteria.lastName) params.lastName = searchCriteria.lastName;
      if (searchCriteria.username) params.username = searchCriteria.username;

      const response = await axiosInstance.get(ENDPOINTS.USERS.SEARCH.USER_SERVICE, {
        params,
        timeout: 30000
      });

      if (response.data && response.data.data && response.data.data.length > 0) {
        setSearchResults(response.data.data);
      } else {
        setError('No users found');
      }
    } catch (err) {
      setError('Error searching users: ' + (err.message || 'Unknown error'));
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    const user = searchResults.find(u => u.id === userId);
    setSelectedUser(user);
    setUserDetails({
      chatData: null,
      pollsData: null,
      loadingDetails: true
    });
    setError('');
    setActiveTab('overview');
  
    try {
      const [chatResult, pollsResult] = await Promise.allSettled([
        axiosInstance.get(ENDPOINTS.USERS.SEARCH.CHAT_SERVICE, {
          params: { username: userId },
          timeout: 30000,
          validateStatus: () => true // Don't throw on non-200
        }),
        axiosInstance.get(ENDPOINTS.USERS.SEARCH.POLLS_SERVICE, {
          params: { username: userId },
          timeout: 30000,
          validateStatus: () => true
        })
      ]);
  
      // Process chat result
      let chatData = null;
      if (chatResult.status === 'fulfilled' && chatResult.value.status === 200) {
        chatData = chatResult.value.data;
      } else if (chatResult.status === 'fulfilled') {
        console.error('Chat service returned non-200:', chatResult.value.status);
      } else {
        console.error('Chat service error:', chatResult.reason);
      }
  
      // Process polls result
      let pollsData = null;
      if (pollsResult.status === 'fulfilled' && pollsResult.value.status === 200) {
        pollsData = pollsResult.value.data;
      } else if (pollsResult.status === 'fulfilled') {
        console.error('Polls service returned non-200:', pollsResult.value.status);
      } else {
        console.error('Polls service error:', pollsResult.reason);
      }
  
      const details = {
        chatData,
        pollsData,
        loadingDetails: false
      };
  
      setUserDetails(details);
  
      // Only show error if both services failed
      if (!chatData && !pollsData) {
        setError('Failed to fetch user details from both services');
      } else if (!chatData || !pollsData) {
        // Show warning if only one service succeeded
        setError(
          `Partially loaded: ${!chatData ? 'Chat service failed' : ''} ${!pollsData ? 'Polls service failed' : ''}`.trim()
        );
      }
    } catch (err) {
      setUserDetails(prev => ({
        ...prev,
        loadingDetails: false
      }));
      setError('Error fetching user details: ' + (err.message || 'Unknown error'));
      console.error('Fetch details error:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const renderUserInfo = () => {
    if (!selectedUser) return null;

    const metadata = selectedUser.metadata?.api || {};
    return (
      <div className="user-info-card">
        <div className="user-header">
          {metadata.avatar && (
            <img src={metadata.avatar} alt="Avatar" className="user-avatar" />
          )}
          <div>
            <h3>{metadata.firstName || ''} {metadata.lastName || ''}</h3>
            <p>Username: {selectedUser.id}</p>
            {metadata.created && <p>Created: {formatDate(metadata.created)}</p>}
            {metadata.gender && <p>Gender: {metadata.gender}</p>}
            {metadata.birthday && <p>Birthday: {metadata.birthday}</p>}
            {metadata.roles && metadata.roles.length > 0 && (
              <p>Roles: {metadata.roles.join(', ')}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderChatData = () => {
    if (!userDetails.chatData) return <p>No chat data available</p>;

    const chat = userDetails.chatData.data;
    const user = chat?.user?.data?.attributes;
    const messages = chat?.messages?.data || [];
    const participants = chat?.participants?.data || [];

    return (
      <div className="chat-data">
        {user && (
          <div className="chat-user-info">
            <h4>User Profile from Chat Service</h4>
            <p>Name: {user.name}</p>
            {user.gender && <p>Gender: {user.gender}</p>}
            {user.birthday && <p>Birthday: {user.birthday}</p>}
          </div>
        )}

        <div className="messages-section">
          <h4>Messages ({messages.length})</h4>
          <div className="messages-list">
            {messages.map(msg => (
              <div key={msg.id} className="message-card">
                <div className="message-header">
                  <span className="message-date">{formatDate(msg.attributes.createdAt)}</span>
                  <span className="message-room">Room: {msg.attributes.roomId.substring(0, 8)}...</span>
                </div>
                <p className="message-text">{msg.attributes.text || msg.attributes.sanitizedText || '[Media]'}</p>
                {msg.attributes.media && (
                  <a href={msg.attributes.media} target="_blank" rel="noopener noreferrer" className="media-link">
                    View Media
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="participants-section">
          <h4>Room Participations ({participants.length})</h4>
          <div className="participants-grid">
            {participants.map(part => (
              <div key={`${part.id}-${part.attributes.roomId}`} className="participant-card">
                <p><strong>Room ID:</strong> {part.attributes.roomId.substring(0, 8)}...</p>
                <p><strong>Joined:</strong> {formatDate(part.attributes.joinedAt)}</p>
                <p><strong>Last Active:</strong> {formatDate(part.attributes.lastActivityAt)}</p>
                {part.attributes.bannedAt && (
                  <p className="banned"><strong>Banned:</strong> {formatDate(part.attributes.bannedAt)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPollsData = () => {
    if (!userDetails.pollsData) return <p>No polls data available</p>;

    const polls = userDetails.pollsData.polls?.data || [];
    const contests = userDetails.pollsData.contests?.data || [];

    return (
      <div className="polls-data">
        <div className="polls-section">
          <h4>Polls ({polls.length})</h4>
          <div className="polls-grid">
            {polls.map(poll => (
              <div key={poll.id} className="poll-card">
                <h5>{poll.attributes.title}</h5>
                <div className="poll-details">
                  <p><strong>State:</strong> {poll.attributes.state}</p>
                  <p><strong>Started:</strong> {formatDate(poll.attributes.startedAt)}</p>
                  <p><strong>Ended:</strong> {formatDate(poll.attributes.endedAt) || 'Ongoing'}</p>
                  {poll.attributes.chosenAnswerId && (
                    <p><strong>Chosen Answer ID:</strong> {poll.attributes.chosenAnswerId}</p>
                  )}
                </div>
                {poll.relations?.answers?.data && (
                  <div className="poll-answers">
                    <h6>Answers:</h6>
                    <ul>
                      {poll.relations.answers.data.map(answer => (
                        <li key={answer.id} className={answer.id === poll.attributes.chosenAnswerId ? 'chosen-answer' : ''}>
                          {answer.attributes.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="contests-section">
          <h4>Contests ({contests.length})</h4>
          <div className="contests-grid">
            {contests.map(contest => (
              <div key={contest.id} className="contest-card">
                <h5>{contest.attributes.prize}</h5>
                <div className="contest-details">
                  <p><strong>State:</strong> {contest.attributes.state}</p>
                  <p><strong>Started:</strong> {formatDate(contest.attributes.startedAt)}</p>
                  <p><strong>Ended:</strong> {formatDate(contest.attributes.endedAt)}</p>
                  <p><strong>Winners:</strong> {contest.attributes.nWinners}</p>
                  {contest.attributes.meta?.winners && (
                    <p><strong>Winner IDs:</strong> {contest.attributes.meta.winners.join(', ')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="user-search-container">
      <h2>User Search & Details</h2>
      
      {/* Search Form */}
      <div className="search-form">
        <div className="form-group">
          <label>First Name:</label>
          <input
            type="text"
            name="firstName"
            value={searchCriteria.firstName}
            onChange={handleInputChange}
            placeholder="Enter first name"
          />
        </div>
        <div className="form-group">
          <label>Last Name:</label>
          <input
            type="text"
            name="lastName"
            value={searchCriteria.lastName}
            onChange={handleInputChange}
            placeholder="Enter last name"
          />
        </div>
        <div className="form-group">
          <label>Phone:</label>
          <input
            type="text"
            name="username"
            value={searchCriteria.username}
            onChange={handleInputChange}
            placeholder="Enter phone number"
          />
        </div>
        <button onClick={searchUsers} disabled={loading} className="search-button">
          {loading ? 'Searching...' : 'Search Users'}
        </button>
      </div>

      {/* Error Display */}
      {error && <div className="error-message">{error}</div>}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="search-results">
          <h3>Search Results ({searchResults.length})</h3>
          <div className="results-grid">
            {searchResults.map(user => (
              <div
                key={user.id}
                className={`result-card ${selectedUser?.id === user.id ? 'selected' : ''}`}
                onClick={() => fetchUserDetails(user.id)}
              >
                <h4>{user.id}</h4>
                <div className="user-meta">
                  {user.metadata?.api?.firstName && (
                    <p><strong>Name:</strong> {user.metadata.api.firstName} {user.metadata.api.lastName || ''}</p>
                  )}
                  {user.metadata?.api?.created && (
                    <p><strong>Created:</strong> {new Date(user.metadata.api.created).toLocaleDateString()}</p>
                  )}
                  {user.metadata?.api?.gender && (
                    <p><strong>Gender:</strong> {user.metadata.api.gender}</p>
                  )}
                  {user.metadata?.api?.roles && user.metadata.api.roles.length > 0 && (
                    <p><strong>Roles:</strong> {user.metadata.api.roles.join(', ')}</p>
                  )}
                </div>
                <button className="view-details-btn">View Details</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Details */}
      {selectedUser && (
        <div className="user-details-container">
          <h3>User Details: {selectedUser.id}</h3>
          
          {/* Loading indicator for details */}
          {userDetails.loadingDetails && <div className="loading">Loading user details...</div>}
          
          {/* User Info Card */}
          {!userDetails.loadingDetails && renderUserInfo()}
          
          {/* Tabs for different data sections */}
          {!userDetails.loadingDetails && (
            <div className="tabs">
              <button 
                className={activeTab === 'overview' ? 'active' : ''} 
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button 
                className={activeTab === 'chat' ? 'active' : ''} 
                onClick={() => setActiveTab('chat')}
                disabled={!userDetails.chatData}
              >
                Chat Data {userDetails.chatData && '✓'}
              </button>
              <button 
                className={activeTab === 'polls' ? 'active' : ''} 
                onClick={() => setActiveTab('polls')}
                disabled={!userDetails.pollsData}
              >
                Polls Data {userDetails.pollsData && '✓'}
              </button>
            </div>
          )}
          
          {/* Tab Content */}
          {!userDetails.loadingDetails && (
            <div className="tab-content">
              {activeTab === 'overview' && (
                <div className="overview-tab">
                  <div className="overview-stats">
                    <div className="stat-card">
                      <h4>Chat Service</h4>
                      {userDetails.chatData ? (
                        <>
                          <p>Messages: {userDetails.chatData.data.messages?.data?.length || 0}</p>
                          <p>Rooms: {userDetails.chatData.data.participants?.data?.length || 0}</p>
                        </>
                      ) : (
                        <p className="no-data">No data</p>
                      )}
                    </div>
                    <div className="stat-card">
                      <h4>Polls Service</h4>
                      {userDetails.pollsData ? (
                        <>
                          <p>Polls: {userDetails.pollsData.polls?.data?.length || 0}</p>
                          <p>Contests: {userDetails.pollsData.contests?.data?.length || 0}</p>
                        </>
                      ) : (
                        <p className="no-data">No data</p>
                      )}
                    </div>
                  </div>
                  
                  {userDetails.chatData && (
                    <div className="quick-view">
                      <h4>Recent Messages</h4>
                      {userDetails.chatData.data.messages?.data?.slice(0, 3).map(msg => (
                        <div key={msg.id} className="quick-message">
                          <p>{formatDate(msg.attributes.createdAt)}</p>
                          <p>{msg.attributes.text || msg.attributes.sanitizedText || '[Media]'}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'chat' && userDetails.chatData && renderChatData()}
              {activeTab === 'polls' && userDetails.pollsData && renderPollsData()}
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .user-search-container {
          padding: 20px;
          font-family: Arial, sans-serif;
        }

        .search-form {
          background: #f5f5f5;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          align-items: end;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          margin-bottom: 5px;
          font-weight: bold;
          color: #333;
        }

        .form-group input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .search-button {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
          transition: background 0.3s;
        }

        .search-button:hover {
          background: #0056b3;
        }

        .search-button:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 20px;
          border: 1px solid #f5c6cb;
        }

        .search-results {
          margin-bottom: 30px;
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 15px;
          margin-top: 15px;
        }

        .result-card {
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          padding: 15px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .result-card:hover {
          border-color: #007bff;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .result-card.selected {
          border-color: #28a745;
          background: #f8fff9;
        }

        .result-card h4 {
          margin: 0 0 10px 0;
          color: #333;
          word-break: break-all;
        }

        .user-meta p {
          margin: 5px 0;
          font-size: 14px;
          color: #666;
        }

        .view-details-btn {
          margin-top: 10px;
          padding: 5px 10px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .user-details-container {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }

        .user-info-card {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .user-header {
          display: flex;
          gap: 20px;
          align-items: center;
        }

        .user-avatar {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
        }

        .tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          border-bottom: 1px solid #dee2e6;
          padding-bottom: 10px;
        }

        .tabs button {
          padding: 8px 16px;
          background: none;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          color: #495057;
        }

        .tabs button:hover {
          background: #e9ecef;
        }

        .tabs button.active {
          background: #007bff;
          color: white;
        }

        .tabs button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .tab-content {
          min-height: 200px;
        }

        .overview-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }

        .stat-card {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          border-left: 4px solid #007bff;
        }

        .stat-card h4 {
          margin: 0 0 10px 0;
          color: #333;
        }

        .stat-card p {
          margin: 5px 0;
          color: #666;
        }

        .no-data {
          color: #6c757d;
          font-style: italic;
        }

        .quick-view {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 15px;
        }

        .quick-message {
          padding: 10px;
          border-bottom: 1px solid #eee;
        }

        .quick-message:last-child {
          border-bottom: none;
        }

        .quick-message p {
          margin: 5px 0;
          font-size: 14px;
        }

        .chat-data, .polls-data {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .messages-list {
          max-height: 400px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .message-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          padding: 12px;
        }

        .message-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 12px;
          color: #666;
        }

        .message-text {
          margin: 0;
          word-break: break-word;
        }

        .media-link {
          display: inline-block;
          margin-top: 8px;
          color: #007bff;
          text-decoration: none;
          font-size: 14px;
        }

        .media-link:hover {
          text-decoration: underline;
        }

        .participants-grid, .polls-grid, .contests-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 15px;
        }

        .participant-card, .poll-card, .contest-card {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          padding: 12px;
          font-size: 14px;
        }

        .participant-card p, .poll-details p, .contest-details p {
          margin: 5px 0;
        }

        .banned {
          color: #dc3545;
        }

        .poll-answers {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #dee2e6;
        }

        .poll-answers h6 {
          margin: 0 0 5px 0;
          font-size: 14px;
        }

        .poll-answers ul {
          margin: 0;
          padding-left: 20px;
        }

        .poll-answers li {
          margin: 3px 0;
          font-size: 13px;
        }

        .chosen-answer {
          color: #28a745;
          font-weight: bold;
        }

        h2, h3, h4 {
          color: #333;
        }

        h2 {
          margin-bottom: 20px;
        }

        h3 {
          margin: 20px 0 15px 0;
        }

        h4 {
          margin: 15px 0 10px 0;
        }
      `}</style>
    </div>
  );
};

export default SearchUser;